import React from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Typography from 'components/commercetools-ui/atoms/typography';
import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import styles from './index.module.css';

/**
 * Chronos — a section title/description followed by a responsive grid of cards, each with a
 * (circular) image + title + rich-text description. Maps the legacy `multiple_info_section`
 * (milestones/timeline) where each `multiple_info` item becomes a card.
 */
export default function ChronosRendering({ content }: { content: GenericSectionContent }) {
  const { cards = [], title, description, content_alignment, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'ChronosRendering');

  const textAlignmentClass =
    content_alignment === 'Left' ? 'text-left' : content_alignment === 'Right' ? 'text-right' : 'text-center';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div
        className={`pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80 ${[
          additional_css_class_names,
          uniqueClass,
        ]
          .filter(Boolean)
          .join(' ')}`}
      >
        {title && (
          <Typography
            as="h3"
            className={`mb-36 md:mb-48 text-32 xl:text-36 2xl:text-48 font-medium leading-8 xl:leading-[2.25rem] 2xl:leading-[3.25rem] text-[#090909] ${textAlignmentClass}`}
          >
            {title}
          </Typography>
        )}
        {description && (
          <div
            className={`mb-24 text-16 md:text-18 2xl:text-20 font-normal leading-6 text-[#333] ${textAlignmentClass}`}
            dangerouslySetInnerHTML={{ __html: sanitize(description) }}
          />
        )}
        {cards.length > 0 && (
          <div className="grid grid-cols-1 gap-32 sm:grid-cols-2 lg:grid-cols-4 lg:gap-40">
            {cards.map((card: MediaCard, index: number) => (
              <div key={index} className="flex flex-col items-center text-center">
                {card?.desktop_image?.url && (
                  <img
                    className="h-[120px] w-[120px] rounded-full object-cover 2xl:h-[150px] 2xl:w-[150px]"
                    src={card.desktop_image.url}
                    alt={card.desktop_image?.description ?? card.title ?? ''}
                  />
                )}
                {card?.title && (
                  <Typography as="h4" className="mt-16 text-20 font-semibold text-primary-black 2xl:text-26">
                    {card.title}
                  </Typography>
                )}
                {card?.description && (
                  <div
                    className="mt-8 text-14 font-normal leading-6 text-[#333] [&>p]:mb-8 2xl:text-16"
                    dangerouslySetInnerHTML={{ __html: sanitize(card.description) }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
